/**
 * Çoklu GTFS registry — yükle / switch / analiz hydrate / merge.
 *
 * Bellek kuralı: aktif olmayan feed'lerde ağır tablolar TUTULMAZ.
 * Yalnızca zip blob + lite meta kalır. Analiz sırasında tablolar
 * tek tek yüklenip merge edilir, hemen serbest bırakılır.
 */
import { markRaw } from 'vue'
import api from '@/services/api.js'
import { useGtfsStore } from '@/stores'
import {
  ANALYSIS_TABLE_NAMES,
  createLocalFeedId,
} from '@/composables/gtfs/gtfsFeedTypes.js'
import {
  appendFeedTablesToMerged,
  filterGtfsTablesToVisibleRoutes,
} from '@/composables/gtfs/mergeGtfsTablesForAnalysis.js'
import { hydrateDeferredGtfsTables } from '@/composables/gtfs/hydrateGtfsTables.js'
import { loadGtfsTablesFromZipBlob } from '@/composables/gtfs/loadGtfsTablesFromZip.js'

/** Soft uyarı eşiği — tarayıcı OOM riski */
export const FEED_MEMORY_SOFT_LIMIT = 3

/** Aynı feed sayısında tekrar uyarı basma */
let lastMemoryWarnCount = 0

const ANALYSIS_LOAD_TABLES = ANALYSIS_TABLE_NAMES.map((n) => `${n}.txt`)

/**
 * Zip'ten analiz tablolarını yükler (editör state'ine dokunmadan).
 * İstemci JSZip — sunucu /tables/load spam'i yok (429 önlemi).
 *
 * @param {File|Blob} file
 * @param {string[]} [tableFiles]
 */
export async function loadTablesFromZipFile(file, tableFiles = ANALYSIS_LOAD_TABLES) {
  const names = (tableFiles || []).map((t) => String(t).replace(/\.txt$/i, ''))
  return loadGtfsTablesFromZipBlob(file, names.length ? names : ANALYSIS_TABLE_NAMES)
}

/**
 * Aktif feed satırlarını geçici obje olarak okur (registry'ye yazmaz).
 * @param {object} gtfsStore
 */
function readActiveTablesForAnalysis(gtfsStore) {
  /** @type {Record<string, Record<string, unknown>[]>} */
  const tables = {}
  for (const name of ANALYSIS_TABLE_NAMES) {
    tables[name] = gtfsStore.getTableRows(name)
  }
  return tables
}

/**
 * Feed üzerindeki ağır analiz cache'ini bırakır.
 * @param {object} gtfsStore
 * @param {string} [localId] — yoksa tüm feed'ler
 */
export function clearFeedAnalysisCaches(gtfsStore, localId) {
  const list = localId
    ? [gtfsStore.findFeed(localId)].filter(Boolean)
    : gtfsStore.feeds || []
  for (const entry of list) {
    entry.analysisTables = null
    entry.analysisHydrated = false
  }
}

/**
 * Aktif feed'i switch öncesi park et: dirty ise zip'e yaz, ağır cache tutma.
 * @param {object} gtfsStore
 * @param {object|null} gtfsView
 */
export async function parkActiveFeedBeforeSwitch(gtfsStore, gtfsView = null) {
  const entry = gtfsStore.activeFeed
  if (!entry || !gtfsStore.currentGTFSData) return { success: true }

  entry.stats = {
    totalStops: gtfsStore.stats.totalStops,
    totalRoutes: gtfsStore.stats.totalRoutes,
    totalTrips: gtfsStore.stats.totalTrips,
  }
  entry.tableCounts = { ...gtfsStore.tableCounts }

  if (gtfsStore.pendingDirty) {
    const { persistGtfsFeed } = await import(
      '@/composables/gtfs/useGTFSEditorPersist.js'
    )
    const result = await persistGtfsFeed({
      gtfsStore,
      gtfsView,
      download: false,
      clearDirty: true,
      saveToDb: false,
    })
    if (!result.success) {
      return {
        success: false,
        error: result.error || 'Değişiklikler zip’e yazılamadı',
      }
    }
  } else {
    const zip =
      gtfsStore.currentGTFSData?.originalFile ||
      entry.zipFile ||
      null
    if (zip) entry.zipFile = markRaw(zip)
  }

  // Kritik: ağır tabloları registry'de tutma
  entry.analysisTables = null
  entry.analysisHydrated = false
  return { success: true }
}

/**
 * Dahil feed'lerin hepsi DB'de (dbId) ve aktif dirty değilse feed_ids[] kullanılabilir.
 */
export function canUseBackendFeedIds(gtfsStore = useGtfsStore()) {
  const included = gtfsStore.includedFeeds || []
  if (!included.length) return false
  if (gtfsStore.pendingDirty) return false
  if (!included.every((f) => f.dbId != null && Number(f.dbId) > 0)) return false
  // Aynı dbId birden fazla registry kaydında varsa feed_ids tekilleşir → zip yoluna düş
  const ids = included.map((f) => Number(f.dbId))
  return new Set(ids).size === ids.length
}

/**
 * Analiz isteği gövdesi: mümkünse feed_ids (2B), değilse zip multipart (413 önlemi),
 * son çare merged tables JSON.
 * @param {{ tableCols?: Record<string, string[]>, tableNames?: string[], gtfsView?: object }} [opts]
 * @returns {Promise<{
 *   success: boolean
 *   mode: 'feed_ids'|'zips'|'tables'|null
 *   feed_ids?: number[]
 *   files?: { blob: Blob, filename: string, localId: string }[]
 *   tables?: Record<string, unknown[]>
 *   feedCount: number
 *   stopCount: number
 *   namespaced: boolean
 *   error?: string
 * }>}
 */
export async function resolveAnalysisInput(opts = {}) {
  const gtfsStore = useGtfsStore()
  const included = gtfsStore.includedFeeds
  if (!included.length) {
    return {
      success: false,
      mode: null,
      error: 'Analize dahil feed yok',
      feedCount: 0,
      stopCount: 0,
      namespaced: false,
    }
  }

  // feed_ids ve ham zip sunucuda tüm hatları görür. Gizli hat varken
  // sözleşme aynı kalır: mevcut tables moduna düşülür ve filtre istemcide uygulanır.
  const scopeHidden = (gtfsStore.hiddenRouteIds || []).length > 0

  if (!scopeHidden && canUseBackendFeedIds(gtfsStore)) {
    const feed_ids = included.map((f) => Number(f.dbId))
    return {
      success: true,
      mode: 'feed_ids',
      feed_ids,
      feedCount: feed_ids.length,
      stopCount: included.reduce(
        (n, f) => n + (f.stats?.totalStops || f.tableCounts?.stops || 0),
        0
      ),
      namespaced: feed_ids.length > 1,
    }
  }

  // Zip multipart: JSON tables'ın 10–50× şişmesini ve 413'ü önler.
  // Gizli hat varsa zip ham gider; bu yüzden tables yoluna düşülür.
  let zipError = ''
  if (!scopeHidden) {
    const zipped = await collectAnalysisZipFiles({ gtfsView: opts.gtfsView })
    if (zipped.success && zipped.files?.length) {
      return {
        success: true,
        mode: 'zips',
        files: zipped.files,
        feedCount: zipped.files.length,
        stopCount: included.reduce(
          (n, f) => n + (f.stats?.totalStops || f.tableCounts?.stops || 0),
          0
        ),
        namespaced: zipped.files.length > 1,
      }
    }
    zipError = zipped.error || ''
  }

  const merged = await collectMergedAnalysisTables(opts)
  if (!merged.success) {
    return {
      success: false,
      mode: 'tables',
      error: zipError || merged.error,
      feedCount: merged.feedCount,
      stopCount: 0,
      namespaced: merged.namespaced,
    }
  }
  return {
    success: true,
    mode: 'tables',
    tables: merged.tables,
    feedCount: merged.feedCount,
    stopCount: merged.stopCount,
    namespaced: merged.namespaced,
  }
}

/**
 * Dahil feed'ler için analiz zip blob'larını toplar (dirty aktif → önce zip yaz).
 * @param {{ gtfsView?: object }} [opts]
 */
export async function collectAnalysisZipFiles(opts = {}) {
  const gtfsStore = useGtfsStore()
  const included = gtfsStore.includedFeeds
  if (!included.length) {
    return { success: false, error: 'Analize dahil feed yok', files: [] }
  }

  /** @type {{ blob: Blob, filename: string, localId: string }[]} */
  const files = []

  try {
    for (const feed of included) {
      let blob = feed.zipFile || null

      if (
        feed.localId === gtfsStore.activeFeedId &&
        gtfsStore.pendingDirty
      ) {
        const { persistGtfsFeed } = await import(
          '@/composables/gtfs/useGTFSEditorPersist.js'
        )
        const result = await persistGtfsFeed({
          gtfsStore,
          gtfsView: opts.gtfsView,
          download: false,
          clearDirty: true,
          saveToDb: false,
        })
        if (!result.success || !result.file) {
          throw new Error(
            result.error || `"${feed.name}" zip’e yazılamadı`
          )
        }
        blob = result.file
        feed.zipFile = markRaw(result.file)
      }

      if (!blob && feed.localId === gtfsStore.activeFeedId) {
        blob =
          gtfsStore.currentGTFSData?.originalFile ||
          feed.zipFile ||
          null
      }

      if (!blob) {
        throw new Error(`"${feed.name}" için zip dosyası yok`)
      }

      files.push({
        blob,
        filename: feed.name?.endsWith('.zip')
          ? feed.name
          : `${feed.name || feed.localId}.zip`,
        localId: feed.localId,
      })
    }
    return { success: true, files }
  } catch (error) {
    return {
      success: false,
      error: error?.message || String(error),
      files: [],
    }
  }
}

/**
 * Dahil feed'leri tek tek yükleyip birleştirir; kaynakları hemen bırakır.
 * Peak bellek ≈ aktif feed + 1 geçici feed + merge çıktısı.
 *
 * @param {{ tableCols?: Record<string, string[]>, tableNames?: string[] }} [opts]
 */
export async function collectMergedAnalysisTables(opts = {}) {
  const gtfsStore = useGtfsStore()
  const included = gtfsStore.includedFeeds
  if (!included.length) {
    return {
      success: false,
      error: 'Analize dahil feed yok',
      tables: {},
      feedCount: 0,
      stopCount: 0,
      namespaced: false,
    }
  }

  gtfsStore.analysisHydrating = true
  /** @type {Record<string, Record<string, unknown>[]>} */
  const merged = {}
  const namespaced = included.length > 1

  try {
    for (const feed of included) {
      let tables = null
      try {
        if (
          feed.localId === gtfsStore.activeFeedId &&
          gtfsStore.currentGTFSData
        ) {
          tables = readActiveTablesForAnalysis(gtfsStore)
        } else {
          if (!feed.zipFile) {
            throw new Error(`"${feed.name}" için zip dosyası yok`)
          }
          tables = await loadTablesFromZipFile(feed.zipFile)
        }

        appendFeedTablesToMerged(
          merged,
          {
            localId: feed.localId,
            name: feed.name,
            dbId: feed.dbId,
            tables,
          },
          {
            namespaced,
            tableCols: opts.tableCols,
            tableNames: opts.tableNames,
          }
        )
      } finally {
        // Geçici satırları bırak; registry'ye yazma
        tables = null
        if (feed.localId !== gtfsStore.activeFeedId) {
          feed.analysisTables = null
          feed.analysisHydrated = false
        }
      }
    }

    // Aktif feed için de kalıcı analysisTables tutma
    clearFeedAnalysisCaches(gtfsStore)

    const filtered = filterGtfsTablesToVisibleRoutes(
      merged,
      gtfsStore.hiddenRouteIds,
      { scopeFeedLocalId: gtfsStore.activeFeedId }
    )

    return {
      success: true,
      tables: filtered.tables,
      feedCount: included.length,
      stopCount: filtered.stopCount,
      namespaced,
    }
  } catch (error) {
    clearFeedAnalysisCaches(gtfsStore)
    return {
      success: false,
      error: error?.message || String(error),
      tables: {},
      feedCount: included.length,
      stopCount: 0,
      namespaced,
    }
  } finally {
    gtfsStore.analysisHydrating = false
  }
}

/**
 * Dirty guard: true = devam et.
 */
export function confirmDiscardDirty(gtfsStore, message) {
  if (!gtfsStore?.pendingDirty) return true
  return window.confirm(
    message ||
      'Kaydedilmemiş değişiklikler var. Devam ederseniz zip’e yazılıp bellekten düşülür. Devam?'
  )
}

function warnFeedMemoryIfNeeded(gtfsStore, showToast) {
  const n = gtfsStore.feeds?.length || 0
  if (n < FEED_MEMORY_SOFT_LIMIT) return
  if (lastMemoryWarnCount === n) return
  lastMemoryWarnCount = n
  const msg =
    `${n} GTFS yüklü. Bellek için yalnızca aktif feed açık tutulur; ` +
    `analizde feed’ler sırayla (istemci zip) okunur. Gerekirse bazılarını listeden kaldırın.`
  if (typeof showToast === 'function') showToast('warning', msg)
  else console.warn(msg)
}

/**
 * Aktif feed'i park edip başka feed'in zip'ini editöre yükler.
 * @param {string} localId
 * @param {{ gtfsView?: object, skipDirtyConfirm?: boolean }} [opts]
 */
export async function activateFeedById(localId, opts = {}) {
  const gtfsStore = useGtfsStore()
  const entry = gtfsStore.findFeed(localId)
  if (!entry) return { success: false, error: 'Feed bulunamadı' }
  if (gtfsStore.activeFeedId === localId && gtfsStore.currentGTFSData) {
    return { success: true, skipped: true }
  }
  if (!opts.skipDirtyConfirm && !confirmDiscardDirty(gtfsStore)) {
    return { success: false, cancelled: true }
  }
  if (!entry.zipFile) {
    return { success: false, error: 'Feed zip dosyası yok — yeniden yükleyin' }
  }

  if (gtfsStore.activeFeedId && gtfsStore.currentGTFSData) {
    const parked = await parkActiveFeedBeforeSwitch(gtfsStore, opts.gtfsView)
    if (!parked.success) return parked
  }

  // Diğer feed cache'lerini de temizle
  clearFeedAnalysisCaches(gtfsStore)

  const file = entry.zipFile
  const result = await api.inspectGtfs(file, { lite: true })
  if (!result?.success) {
    return { success: false, error: result?.error || 'Inspect başarısız' }
  }

  const tables = result.meta?.tables ?? result.tables_info ?? {}
  const meta = result.meta?.tables
    ? result.meta
    : {
        ...(result.meta || {}),
        tables,
        stops_count:
          tables['stops.txt']?.record_count ?? result.meta?.stops_count ?? 0,
        routes_count:
          tables['routes.txt']?.record_count ?? result.meta?.routes_count ?? 0,
        trips_count:
          tables['trips.txt']?.record_count ?? result.meta?.trips_count ?? 0,
      }

  const payload = {
    ...result,
    meta,
    originalFile: file,
  }

  gtfsStore.setInspectResult({
    result: payload,
    fileName: entry.fileName || (file.name || entry.name),
    feedMeta: {
      localId: entry.localId,
      dbId: entry.dbId,
      name: entry.name,
      source: entry.source,
      senaryoId: entry.senaryoId,
      gunTipi: entry.gunTipi,
      agencyName: entry.agencyName,
      replaceActive: true,
    },
  })

  if (opts.gtfsView?.currentGTFSData) {
    const ref = opts.gtfsView.currentGTFSData
    if ('value' in ref) ref.value = payload
    else opts.gtfsView.currentGTFSData = payload
  }
  if (opts.gtfsView?.currentGTFSFile) {
    const ref = opts.gtfsView.currentGTFSFile
    if ('value' in ref) ref.value = file
    else opts.gtfsView.currentGTFSFile = file
  }

  await hydrateDeferredGtfsTables(gtfsStore, file, { includeShapes: true })
  return { success: true }
}

/**
 * Yeni zip'i registry'ye ekler ve aktif yapar.
 */
export async function addUploadFeedFromFile(file, opts = {}) {
  const gtfsStore = useGtfsStore()
  if (!opts.skipDirtyConfirm && !confirmDiscardDirty(gtfsStore)) {
    return { success: false, cancelled: true }
  }

  if (gtfsStore.activeFeedId && gtfsStore.currentGTFSData) {
    const parked = await parkActiveFeedBeforeSwitch(gtfsStore, opts.gtfsView)
    if (!parked.success) return parked
  }
  clearFeedAnalysisCaches(gtfsStore)

  const localId = createLocalFeedId()
  const result = await api.inspectGtfs(file, { lite: true })
  if (!result?.success) {
    return { success: false, error: result?.error || 'Inspect başarısız' }
  }

  const tables = result.meta?.tables ?? result.tables_info ?? {}
  const meta = result.meta?.tables
    ? result.meta
    : {
        ...(result.meta || {}),
        tables,
        stops_count:
          tables['stops.txt']?.record_count ?? result.meta?.stops_count ?? 0,
        routes_count:
          tables['routes.txt']?.record_count ?? result.meta?.routes_count ?? 0,
        trips_count:
          tables['trips.txt']?.record_count ?? result.meta?.trips_count ?? 0,
      }

  const payload = { ...result, meta, originalFile: file }
  const name = opts.name || file.name || 'Yüklenen GTFS'

  gtfsStore.activeFeedId = null
  gtfsStore.setInspectResult({
    result: payload,
    fileName: file.name || name,
    feedMeta: {
      localId,
      name,
      source: opts.source || 'upload',
      dbId: opts.dbId ?? null,
      senaryoId: opts.senaryoId ?? null,
      gunTipi: opts.gunTipi ?? null,
      agencyName: opts.agencyName ?? null,
      replaceActive: false,
    },
  })

  if (opts.gtfsView?.currentGTFSData) {
    const ref = opts.gtfsView.currentGTFSData
    if ('value' in ref) ref.value = payload
    else opts.gtfsView.currentGTFSData = payload
  }
  if (opts.gtfsView?.currentGTFSFile) {
    const ref = opts.gtfsView.currentGTFSFile
    if ('value' in ref) ref.value = file
    else opts.gtfsView.currentGTFSFile = file
  }

  await hydrateDeferredGtfsTables(gtfsStore, file, { includeShapes: true })
  warnFeedMemoryIfNeeded(gtfsStore, opts.gtfsView?.showToast)
  return { success: true, localId: gtfsStore.activeFeedId }
}

/**
 * Senaryo DB feed'ini registry'ye ekler (aynı dbId varsa aktive eder).
 */
export async function addScenarioFeedFromDb(feedId, opts = {}) {
  const gtfsStore = useGtfsStore()
  const existing = gtfsStore.findFeedByDbId(Number(feedId))
  if (existing?.zipFile) {
    return activateFeedById(existing.localId, opts)
  }

  if (!opts.skipDirtyConfirm && !confirmDiscardDirty(gtfsStore)) {
    return { success: false, cancelled: true }
  }

  const result = await api.getGtfsFeed(feedId, true)
  if (!result?.success || !result.feed?.zip_data) {
    return { success: false, error: result?.error || 'Feed yüklenemedi' }
  }

  const feed = result.feed
  const binary = atob(feed.zip_data)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  const file = new File(
    [bytes],
    feed.zip_name || `gtfs_${feedId}.zip`,
    { type: 'application/zip' }
  )

  return addUploadFeedFromFile(file, {
    ...opts,
    skipDirtyConfirm: true,
    name: feed.name || file.name,
    source: 'scenario',
    dbId: feed.id,
    senaryoId: feed.senaryo_id,
    gunTipi: feed.gun_tipi,
    agencyName: feed.agency_name,
  })
}

/**
 * Feed kaldır; aktifse sonrakini yükle.
 */
export async function removeFeedFromRegistry(localId, opts = {}) {
  const gtfsStore = useGtfsStore()
  if (gtfsStore.activeFeedId === localId) {
    if (!opts.skipDirtyConfirm && !confirmDiscardDirty(gtfsStore)) {
      return { success: false, cancelled: true }
    }
  }
  const entry = gtfsStore.findFeed(localId)
  if (entry) {
    entry.analysisTables = null
    entry.zipFile = null
  }
  const result = gtfsStore.removeFeed(localId)
  if (!result.removed) return { success: false, error: 'Feed yok' }
  if (result.needReload && result.nextId) {
    return activateFeedById(result.nextId, { ...opts, skipDirtyConfirm: true })
  }
  return { success: true }
}
